import { UNAVAILABLE_REASONS } from "_shared/_api/by-ids/by-ids-result";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import BookmarkedCompany from "marketplace/bookmarks/company-bookmarks/bookmarked-company";
import BookmarkedCompanySchema from "marketplace/bookmarks/company-bookmarks/_schema/bookmarked-company-schema";
import Company from "marketplace/companies/company";
import CompanySchema from "marketplace/companies/company-schema";
import { z } from "zod";

export type BookmarkedCompanyListItemJSON = z.infer<typeof BookmarkedCompanyListItemSchema.BookmarkedCompanyListItemJSONZ>;
export type BookmarkedCompanyListItemParams = z.infer<typeof BookmarkedCompanyListItemSchema.BookmarkedCompanyListItemZ>;

/**
 * A row of the list endpoint can only be refused, never missing: the bookmark exists, so the row
 * stays. It is module-private so that both public schemas can read it.
 */
const listItemUnavailableReasonZ = z.enum(UNAVAILABLE_REASONS).extract(["unauthorized"]);

/**
 * One row of `GET marketplace/company-bookmarks`: the bookmark, and the company when the caller may
 * read it. When the caller may not, `company` is null and `unavailableReason` is "unauthorized".
 */
export default class BookmarkedCompanyListItemSchema {
  public static readonly BookmarkedCompanyListItemJSONZ = z.object({
    ...BookmarkedCompanySchema.BookmarkedCompanyJSONZ.shape,
    company: CompanySchema.CompanyJSONZ.nullable(),
    unavailableReason: listItemUnavailableReasonZ.nullable(),
  });
  public static readonly BookmarkedCompanyListItemZ = z.object({
    bookmark: z.instanceof(BookmarkedCompany),
    company: z.instanceof(Company).optional(),
    unavailableReason: listItemUnavailableReasonZ.optional(),
  });

  public static validate(values: unknown): BookmarkedCompanyListItemParams {
    return parseOrThrow(BookmarkedCompanyListItemSchema.BookmarkedCompanyListItemZ, values, {
      logError: true,
      schemaName: "BookmarkedCompanyListItemSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): BookmarkedCompanyListItemJSON {
    return parseOrThrow(BookmarkedCompanyListItemSchema.BookmarkedCompanyListItemJSONZ, values, {
      logError: true,
      schemaName: "BookmarkedCompanyListItemSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is BookmarkedCompanyListItemJSON {
    try {
      BookmarkedCompanyListItemSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }
}
